(() => {
  const selector = '[data-section-type="product"] form.product-form[data-product-form]';
  const observed = new WeakSet();
  const observedBundles = new WeakSet();
  const pending = new WeakSet();
  const observedSections = new WeakSet();
  const observedAppBlocks = new WeakSet();
  const selectionStates = new WeakMap();
  const purchaseButtons = new WeakMap();

  function stateFor(form) {
    if (!selectionStates.has(form)) selectionStates.set(form, { tiers: new Map(), applying: false, frames: 0, mainPending: false, rowPending: false });
    return selectionStates.get(form);
  }

  function rowControls(selected) {
    return Array.from(selected?.querySelectorAll('.kaching-bundles__bar-variant-selects') || [])
      .map(row => Array.from(row.querySelectorAll('select[data-name]')));
  }

  function rowValues(selected) {
    return rowControls(selected).map(row => Object.fromEntries(row.map(control => [control.dataset.name, control.value])));
  }

  function tierFor(state, selected) {
    const key = selected?.dataset.dealBarId;
    if (!key) return null;
    if (!state.tiers.has(key)) state.tiers.set(key, {
      rows: rowValues(selected), explicit: rowControls(selected).map(() => new Set())
    });
    return state.tiers.get(key);
  }

  function setPurchasePending(form, busy) {
    if (form.dataset.bundleSelectionSync !== (busy ? 'updating' : 'ready')) {
      form.dataset.bundleSelectionSync = busy ? 'updating' : 'ready';
    }
    form.querySelectorAll('[data-add-to-cart]').forEach(button => {
      if (busy) {
        if (!purchaseButtons.has(button)) purchaseButtons.set(button, {
          inert: button.inert, ariaDisabled: button.getAttribute('aria-disabled')
        });
        if (!button.inert) button.inert = true;
        if (button.getAttribute('aria-disabled') !== 'true') button.setAttribute('aria-disabled', 'true');
        if (button.getAttribute('aria-busy') !== 'true') button.setAttribute('aria-busy', 'true');
      } else if (purchaseButtons.has(button)) {
        const original = purchaseButtons.get(button);
        button.inert = original.inert;
        if (!button.disabled) {
          if (original.ariaDisabled === null) button.removeAttribute('aria-disabled');
          else button.setAttribute('aria-disabled', original.ariaDisabled);
        }
        button.removeAttribute('aria-busy');
        purchaseButtons.delete(button);
      }
    });
  }

  function rememberRow(form, event) {
    const state = stateFor(form);
    const control = event.target;
    if (state.applying || !control.matches?.('.kaching-bundles-option[data-name]')) return;
    const app = form.querySelector('kaching-bundles-block');
    if (!event.isTrusted && (state.mainPending ||
      app?.getAttribute('data-native-variant-change-in-progress') === 'true')) return;
    const selected = control.closest('.kaching-bundles__bar');
    const tier = tierFor(state, selected);
    const rows = rowControls(selected);
    const index = rows.findIndex(row => row.includes(control));
    if (!tier || index < 0) return;
    tier.rows = rowValues(selected);
    tier.explicit[index] ||= new Set();
    tier.explicit[index].add(control.dataset.name);
    state.rowPending = true;
    state.frames = 2;
    setPurchasePending(form, true);
    schedule(form);
  }

  function rememberMain(section, event) {
    const target = event.target;
    if (!target.closest?.('.product__variants-wrapper')) return;
    const form = section.querySelector('form.product-form[data-product-form]');
    if (!form?.querySelector('kaching-bundle')) return;
    const state = stateFor(form);
    if (state.applying) return;
    let control = target;
    let value = target.value;
    if (event.type === 'click') {
      const button = target.closest('[data-button][data-option-value]');
      if (!button) return;
      const option = button.closest('[data-product-option]')?.dataset.productOption;
      control = Array.from(section.querySelectorAll('.product__variants-wrapper select')).find(input => input.dataset.index === option);
      value = button.dataset.optionValue;
    } else {
      if (!control.matches('select[name^="options["], input[name^="options["]')) return;
      // Bundle row changes also update the native picker. They must not become
      // instructions to overwrite the other pieces in that deliberate bundle.
      const app = form.querySelector('kaching-bundles-block');
      if (!event.isTrusted && (state.rowPending ||
        app?.getAttribute('current-variant-source') === 'bundle')) return;
    }
    const name = control?.name?.match(/^options\[(.+)\]$/)?.[1];
    if (!name || value === undefined) return;
    const selected = selectedBar(form.querySelector('.kaching-bundles__block'));
    tierFor(state, selected);
    state.tiers.forEach(tier => tier.rows.forEach((row, index) => {
      if (!tier.explicit[index]?.has(name) && Object.hasOwn(row, name)) row[name] = value;
    }));
    state.mainPending = true;
    state.rowPending = false;
    state.frames = 2;
    setPurchasePending(form, true);
    schedule(form);
  }

  function reconcileSelection(form, selected) {
    const section = form.closest('[data-section-type="product"]');
    const app = form.querySelector('kaching-bundles-block');
    const state = stateFor(form);
    const nativeBusy = section?.hasAttribute('data-variant-loading') ||
      app?.getAttribute('data-native-variant-change-in-progress') === 'true';
    if (nativeBusy) return true;
    const tier = tierFor(state, selected);
    const rows = rowControls(selected);
    if (tier && rows.length === tier.rows.length) {
      for (let index = 0; index < rows.length; index++) {
        for (const control of rows[index]) {
          const desired = tier.rows[index]?.[control.dataset.name];
          if (desired === undefined || control.value === desired) continue;
          const option = Array.from(control.options).find(option => option.value === desired && !option.disabled);
          if (!option) {
            // Let the app's availability rules govern invalid combinations.
            tier.rows[index][control.dataset.name] = control.value;
            continue;
          }
          state.applying = true;
          try {
            control.value = desired;
            control.dispatchEvent(new Event('change', { bubbles: true }));
          } finally { state.applying = false; }
          state.frames = 2;
          schedule(form);
          return true; // Re-read rebuilt controls before the next field.
        }
      }
    }
    if (state.frames > 0) {
      state.frames--;
      schedule(form);
      return true;
    }
    state.mainPending = false;
    state.rowPending = false;
    return false;
  }
  function bundleSettings(productId) {
    const script = document.querySelector(`script.kaching-bundles-deal-block-settings[data-product-id="${productId}"]`);
    try { return JSON.parse(script?.textContent || 'null'); }
    catch (_) { return null; }
  }

  function selectedBar(block) {
    return block?.querySelector('input[type="radio"]:checked')?.closest('.kaching-bundles__bar');
  }

  function moneyValue(text) {
    // The widget renders the correct market currency. Parse only to check that
    // its like-for-like comparison exceeds its total; never calculate prices here.
    const amount = text?.replace(/[^\d.,]/g, '');
    if (!amount) return NaN;
    const lastDot = amount.lastIndexOf('.');
    const lastComma = amount.lastIndexOf(',');
    const decimal = Math.max(lastDot, lastComma);
    const hasDecimals = decimal >= 0 && amount.length - decimal - 1 === 2;
    return Number(hasDecimals
      ? amount.slice(0, decimal).replace(/[.,]/g, '') + '.' + amount.slice(decimal + 1)
      : amount.replace(/[.,]/g, ''));
  }


  const priceVariants = new WeakMap();

  function syncPurchaseLabel(form, selected, bundleBusy, settings) {
    const button = form.querySelector('.product-form__cart-submit');
    const prices = button?.querySelector('[data-zorura-atc-prices]');
    if (!prices) return;
    const updateText = (element, text) => {
      if (element && element.textContent !== text) element.textContent = text;
    };
    const price = prices.querySelector('[data-zorura-atc-price]');
    const comparison = prices.querySelector('[data-zorura-atc-compare]');
    const enabled = !button.disabled && button.getAttribute('aria-disabled') !== 'true';
    if (prices.hidden !== !enabled) prices.hidden = !enabled;
    let amount = '';
    let compare = '';
    const section = form.closest('[data-section-type="product"]');
    const busy = bundleBusy || section?.hasAttribute('data-variant-loading');
    if (busy) {
      if (!prices.hidden) prices.hidden = true;
    } else if (selected && form.classList.contains('zorura-bundle-ready')) {
      amount = selected.querySelector('.kaching-bundles__bar-price')?.textContent.trim() || '';
      const candidate = selected.querySelector('.kaching-bundles__bar-full-price')?.textContent.trim();
      if (settings?.showPricesPerItem === false && moneyValue(candidate) > moneyValue(amount)) compare = candidate;
    } else {
      if (!priceVariants.has(form)) {
        try { priceVariants.set(form, JSON.parse(form.querySelector('[data-zorura-button-variants]')?.textContent || '[]')); }
        catch (_) { priceVariants.set(form, []); }
      }
      const id = form.querySelector('[name="id"]')?.value;
      const variant = priceVariants.get(form).find(item => String(item.id) === id);
      const quantity = Math.max(1, Number(form.querySelector('[name="quantity"]')?.value) || 1);
      if (variant) {
        const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: prices.dataset.currency || 'USD' });
        amount = money.format(variant.price * quantity / 100);
        if (variant.compare_at_price > variant.price) compare = money.format(variant.compare_at_price * quantity / 100);
      }
    }
    updateText(price, amount);
    updateText(comparison, compare);
    if (comparison.hidden !== !compare) comparison.hidden = !compare;
    if (!amount && !prices.hidden) prices.hidden = true;
    const label = button.querySelector('[data-add-to-cart-text]')?.textContent.trim();
    const aria = [label, enabled && !busy ? amount : '', enabled && !busy ? compare : ''].filter(Boolean).join(' | ');
    if (button.getAttribute('aria-label') !== aria) button.setAttribute('aria-label', aria);
    const shipBy = form.querySelector('[data-zorura-ship-by]');
    if (shipBy) {
      const date = new Date();
      let businessDays = 0;
      while (businessDays < 2) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0 && date.getDay() !== 6) businessDays += 1;
      }
      const day = date.getDate();
      const ordinal = day > 10 && day < 14 ? 'th' : ({1:'st',2:'nd',3:'rd'}[day % 10] || 'th');
      const text = day + ordinal + ' ' + date.toLocaleDateString('en-US', { month:'long' });
      updateText(shipBy.querySelector('[data-zorura-ship-date]'), text);
      if (shipBy.hidden !== !enabled) shipBy.hidden = !enabled;
    }
    const total = form.querySelector('[data-zpc-offer-total]');
    if (total && !total.hidden) total.hidden = true;
  }

  function sync(form) {
    const block = form.querySelector('kaching-bundle .kaching-bundles__block');
    const controls = block ? Array.from(block.querySelectorAll('input[type="radio"], [role="radio"], select, button')) : [];
    const actionable = controls.some(control => !control.disabled && control.getAttribute('aria-disabled') !== 'true');
    const visible = block && block.getClientRects().length > 0 && getComputedStyle(block).display !== 'none' && getComputedStyle(block).visibility !== 'hidden';
    form.classList.toggle('zorura-bundle-ready', Boolean(visible && actionable));

    if (block && !observedBundles.has(block)) {
      observedBundles.add(block);
      new MutationObserver(() => schedule(form)).observe(block, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['class', 'checked', 'aria-checked', 'style', 'value']
      });
    }

    const productId = form.querySelector('kaching-bundle')?.getAttribute('product-id');
    const settings = bundleSettings(productId);
    block?.querySelectorAll('.kaching-bundles__bar').forEach(row => {
      const offer = settings?.dealBars?.find(item => item.id === row.dataset.dealBarId);
      const count = Number(offer?.quantity);
      const title = row.querySelector('.kaching-bundles__bar-title');
      if (title && count > 0) {
        const text = count + (count === 1 ? ' Piece' : ' Pieces');
        if (title.textContent.trim() !== text) title.textContent = text;
      }
    });
    const selected = selectedBar(block);
    const busy = block ? reconcileSelection(form, selected) : false;
    setPurchasePending(form, busy);
    const bundlePrice = selected?.querySelector('.kaching-bundles__bar-price')?.textContent?.trim();
    const stickyPrice = form.querySelector('.sticky-atc-bar__price [data-price]');
    const displayedPrice = busy ? 'Updating…' : bundlePrice;
    if (displayedPrice && stickyPrice && stickyPrice.textContent.trim() !== displayedPrice) {
      stickyPrice.textContent = displayedPrice;
    }
    const stickyCompare = form.querySelector('.sticky-atc-bar__price [data-compare-price]');
    const compareLabel = form.querySelector('.sticky-atc-bar__price [data-compare-text]');
    if (block && stickyCompare) {
      const comparison = selected?.querySelector('.kaching-bundles__bar-full-price')?.textContent?.trim();
      // Both values come from the same selected offer, which already accounts for
      // quantity, mixed variants and market currency. Never retain native unit compare.
      const valid = !busy && settings?.showPricesPerItem === false && comparison &&
        moneyValue(comparison) > moneyValue(bundlePrice);
      const text = valid ? comparison : '';
      if (stickyCompare.textContent !== text) stickyCompare.textContent = text;
      if (stickyCompare.hidden !== !valid) stickyCompare.hidden = !valid;
      if (compareLabel && compareLabel.hidden !== !valid) compareLabel.hidden = !valid;
    }
    const bar = form.querySelector('.sticky-atc-bar');
    const summary = bar?.querySelector('.sticky-atc-bar__meta-options');
    const rows = rowValues(selected);
    if (summary && rows.length) {
      const values = rows.map(row => Object.values(row).join(', '));
      const rawText = busy ? 'Updating selection…' :
        (new Set(values).size > 1 ? 'Mixed variants' : values[0]);
      const text = typeof window.zoruraActive29DimensionText === 'function' ? window.zoruraActive29DimensionText(rawText) : rawText;
      if (summary.textContent.trim() !== text) summary.textContent = text;
    }
    const label = bar?.querySelector('[data-sticky-quantity-total]');
    const title = selected?.querySelector('.kaching-bundles__bar-title')?.textContent?.trim().toLowerCase();
    const words = {one:1, two:2, three:3};
    const titleQuantity = title?.match(/^(one|two|three|\d+)\b/);
    const deal = settings?.dealBars?.find(deal => deal.id === selected?.dataset.dealBarId);
    const quantity = Number(deal?.quantity) || (titleQuantity ? (words[titleQuantity[1]] || Number(titleQuantity[1])) : Number(form.querySelector('[name="quantity"]')?.value || 1));
    const unit = 'Piece';
    const text = `${quantity} ${unit}${quantity === 1 ? '' : 's'} · Total`;
    if (label && label.textContent !== text) label.textContent = text;
    const inlineTotal = form.querySelector('[data-zpc-offer-total]');
    if (inlineTotal && !form.querySelector('[data-zorura-atc-prices]')) {
      const inlineButton = form.querySelector('.product-form__item--submit > [data-add-to-cart]');
      if (inlineButton && inlineTotal.nextElementSibling !== inlineButton) inlineButton.before(inlineTotal);
      const value = inlineTotal.querySelector('[data-zpc-offer-price]');
      const label = inlineTotal.querySelector('[data-zpc-offer-label]');
      if (inlineTotal.hidden !== !bundlePrice) inlineTotal.hidden = !bundlePrice;
      if (value && value.textContent !== displayedPrice) value.textContent = displayedPrice || '';
      if (label && label.textContent !== text) label.textContent = text;
    }
    syncPurchaseLabel(form, selected, busy, settings);
    const submit = bar?.querySelector('[data-add-to-cart]');
    if (submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true') {
      const price = stickyPrice?.textContent?.trim();
      const aria = `${submit.querySelector('[data-add-to-cart-text]')?.textContent?.trim()} — ${text}${price ? ' ' + price : ''}`;
      if (submit.getAttribute('aria-label') !== aria) submit.setAttribute('aria-label', aria);
    }
  }

  function schedule(form) {
    if (pending.has(form)) return;
    pending.add(form);
    requestAnimationFrame(() => {
      pending.delete(form);
      if (form.isConnected) sync(form);
    });
  }

  function scan() {
    document.querySelectorAll(selector).forEach(form => {
      if (!observed.has(form)) {
        observed.add(form);
        new MutationObserver(() => schedule(form)).observe(form, {
          childList: true, subtree: true, attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'disabled', 'aria-disabled', 'value']
        });
        form.addEventListener('change', () => schedule(form), true);
        form.addEventListener('input', () => schedule(form), true);
        form.addEventListener('click', () => schedule(form), true);
        form.addEventListener('change', event => rememberRow(form, event), true);
        form.addEventListener('submit', event => {
          sync(form);
          if (form.dataset.bundleSelectionSync === 'updating') {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }, true);
        if (typeof ResizeObserver !== 'undefined') new ResizeObserver(() => schedule(form)).observe(form);
      }
      const section = form.closest('[data-section-type="product"]');
      if (section && !observedSections.has(section)) {
        observedSections.add(section);
        new MutationObserver(() => schedule(form)).observe(section, {
          childList: true, subtree: true, characterData: true, attributes: true,
          attributeFilter: ['data-variant-loading']
        });
        section.addEventListener('click', event => rememberMain(section, event), true);
        section.addEventListener('change', event => rememberMain(section, event), true);
      }
      const app = form.querySelector('kaching-bundles-block');
      if (app && !observedAppBlocks.has(app)) {
        observedAppBlocks.add(app);
        new MutationObserver(() => schedule(form)).observe(app, {
          attributes: true, attributeFilter: ['current-variant-id', 'current-variant-source', 'data-native-variant-change-in-progress']
        });
      }
      schedule(form);
    });
  }

  function start() {
    scan();
    new MutationObserver(scan).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('resize', scan, { passive: true });
    document.addEventListener('shopify:section:load', scan);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) scan(); });
    setInterval(() => document.querySelectorAll(selector).forEach(schedule), 60000);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();


